import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import type { QuarterData, HourlyData, TimeGranularity } from '@/types/piekanalyse';
import { aggregateQuartersToHourly } from '@/lib/piekvermoigenParser';

interface DayChartProps {
  quarters: QuarterData[];
  granularity: TimeGranularity;
  contractedCapacity?: number;
  showReturnDelivery?: boolean;
  height?: number;
  title?: string;
  color?: string;
  returnColor?: string;
}

export const DayChart: React.FC<DayChartProps> = ({
  quarters,
  granularity,
  contractedCapacity,
  showReturnDelivery = false,
  height = 300,
  title,
  color = 'hsl(var(--primary))',
  returnColor = 'hsl(142 71% 45%)',
}) => {
  const chartData = useMemo(() => {
    if (granularity === 'hour') {
      const hourly = aggregateQuartersToHourly(quarters);
      return hourly.map(h => ({
        label: h.hourLabel,
        kWp: h.maxKWp,
        returnKWp: h.maxReturnKWp,
      }));
    }
    
    // Quarter data
    return quarters.map(q => ({
      label: q.time,
      kWp: q.kWp,
      returnKWp: q.returnKWp,
    }));
  }, [quarters, granularity]);

  const maxValue = useMemo(() => {
    const values = [
      ...chartData.map(d => d.kWp),
      ...(showReturnDelivery ? chartData.map(d => d.returnKWp || 0) : []),
      contractedCapacity || 0,
    ];
    const max = Math.max(...values) * 1.15;
    return Math.ceil(max / 50) * 50;
  }, [chartData, showReturnDelivery, contractedCapacity]);

  const stats = useMemo(() => {
    const kWpValues = quarters.map(q => q.kWp);
    const peak = Math.max(...kWpValues);
    const peakQuarter = quarters.find(q => q.kWp === peak);
    const avg = kWpValues.reduce((a, b) => a + b, 0) / kWpValues.length;
    const exceedances = contractedCapacity 
      ? quarters.filter(q => q.kWp > contractedCapacity).length 
      : 0;
    
    return {
      peak: Math.round(peak * 100) / 100,
      peakTime: peakQuarter?.time || '-',
      avg: Math.round(avg * 100) / 100,
      exceedances,
    };
  }, [quarters, contractedCapacity]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-foreground mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-medium">{entry.value?.toFixed(1)} kWp</span>
          </p>
        ))}
      </div>
    );
  };

  // Show every 2nd or 4th label depending on granularity
  const tickInterval = granularity === 'quarter' ? 7 : 1;

  return (
    <div className="space-y-4">
      {title && <h4 className="font-semibold text-sm">{title}</h4>}
      
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={{ stroke: 'hsl(var(--border))' }}
              interval={tickInterval}
              angle={-45}
              textAnchor="end"
              height={50}
            />
            <YAxis
              domain={[0, maxValue]}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={{ stroke: 'hsl(var(--border))' }}
              tickFormatter={(v) => `${v}`}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {contractedCapacity && (
              <ReferenceLine
                y={contractedCapacity}
                stroke="hsl(var(--destructive))"
                strokeDasharray="5 5"
                strokeWidth={2}
                label={{
                  value: `Contract: ${contractedCapacity} kWp`,
                  position: 'right',
                  fill: 'hsl(var(--destructive))',
                  fontSize: 10,
                }}
              />
            )}
            
            <Line
              type="monotone"
              dataKey="kWp"
              name="Afname"
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: color }}
            />
            
            {showReturnDelivery && (
              <Line
                type="monotone"
                dataKey="returnKWp"
                name="Teruglevering"
                stroke={returnColor}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: returnColor }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-muted-foreground text-xs">Piek</p>
          <p className="font-semibold">{stats.peak} kWp</p>
          <p className="text-xs text-muted-foreground">om {stats.peakTime}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-muted-foreground text-xs">Gemiddeld</p>
          <p className="font-semibold">{stats.avg} kWp</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-muted-foreground text-xs">Datapunten</p>
          <p className="font-semibold">{quarters.length}</p>
        </div>
        {contractedCapacity && (
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-muted-foreground text-xs">Overschrijdingen</p>
            <p className={`font-semibold ${stats.exceedances > 0 ? 'text-destructive' : 'text-green-600'}`}>
              {stats.exceedances}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DayChart;
